import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import sha256 from "crypto-js/sha256";

import {
  authApi,
  fcmTokensApi,
  incentivesApi,
  promosApi,
  propertiesApi,
  reportsApi,
} from "../services";
import { RootState } from "../store";
import {
  Session,
  User,
  APIBaseResponse,
  InvalidCredentialsErrorBody,
  RestorePasswordErrorBody,
} from "../../types";

export interface AuthState {
  isAuth: boolean;
  isBlocked: boolean;
  authError: string | null;
  token: string | null;
  user: User | null;
  restorePassword: boolean;
  hashedPass: string | null;
  loginAttempts: number | null;
  /**
   * Credencial del cambio de contraseña **obligatorio**: la emite el `422` del
   * ingreso ante una contraseña temporal, vale 10 minutos y es lo que presenta
   * `POST /auth/recoverPassword`. Con ella y sin sesión, la aplicación muestra
   * la pantalla de cambio en lugar del ingreso.
   */
  passwordChangeToken: string | null;
  /**
   * Credencial del cambio de contraseña **voluntario**: sin
   * `passwordChangeToken`, `recoverPassword` autoriza con el `refreshToken` de
   * la sesión activa. Se persiste con el resto de la sesión, igual que el
   * `sessionToken`: sin él, recargar la página dejaría el cambio desde Perfil
   * respondiendo `401`.
   */
  refreshToken: string | null;
}

const initialState: AuthState = {
  isAuth: false,
  isBlocked: false,
  authError: null,
  token: null,
  user: null,
  restorePassword: false,
  hashedPass: null,
  loginAttempts: null,
  passwordChangeToken: null,
  refreshToken: null,
};

/**
 * Limpia todo lo de la sesión de autenticación a la vez: lo usan el logout
 * (`matchPending`, optimista) y cualquier rechazo de login que no encaje en
 * un caso conocido.
 */
const clearAuthData = (state: AuthState): void => {
  state.isAuth = false;
  state.isBlocked = false;
  state.authError = null;
  state.token = null;
  state.user = null;
  state.restorePassword = false;
  state.hashedPass = null;
  state.passwordChangeToken = null;
  state.refreshToken = null;
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    savePass: (state, action: PayloadAction<string | null>) => {
      const pass = action.payload;
      if (pass === null) state.hashedPass = null;
      else state.hashedPass = sha256(pass).toString();
    },
    clearLoginData: (state) => {
      state.authError = null;
      state.loginAttempts = null;
    },
    /**
     * Sale del cambio obligatorio: al completarlo, o al desistir. Sin el token,
     * la aplicación vuelve a mostrar el ingreso.
     */
    endPasswordChange: (state) => {
      state.passwordChangeToken = null;
      state.hashedPass = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(authApi.endpoints.logout.matchPending, (state, _) => {
        clearAuthData(state);
      })
      .addMatcher(authApi.endpoints.login.matchFulfilled, (state, action) => {
        const { appInfo, user } = action.payload;

        state.isAuth = true;
        state.isBlocked = false;
        state.authError = null;
        state.token = appInfo.sessionToken ?? null;
        state.refreshToken = appInfo.refreshToken ?? null;
        state.user = user;
        state.restorePassword = false;
        state.passwordChangeToken = null;
        state.loginAttempts = null;
      })
      /**
       * Con Java, un rechazo de negocio (credenciales inválidas, bloqueo,
       * cambio de contraseña pendiente) llegaba en un 200 y esto no existía —
       * `matchFulfilled` sí disparaba, y el `switch` sobre `responseCode` hacía
       * el trabajo. NestJS responde con códigos HTTP reales: `login` sí
       * rechaza la promesa, y es este matcher el que reacciona.
       */
      .addMatcher(authApi.endpoints.login.matchRejected, (state, action) => {
        const fetchError = action.payload as FetchBaseQueryError | undefined;

        if (!fetchError || typeof fetchError.status !== "number") {
          // Falla de transporte (sin conexión, timeout): no hay respuesta HTTP
          // que leer. La cubre el `catch` de `useLoginFormManagement`, cuyo
          // `genericErr` pinta `LoginForm`; aquí solo se limpia la sesión.
          clearAuthData(state);
          state.loginAttempts = null;
          return;
        }

        switch (fetchError.status) {
          case 401: {
            const body = fetchError.data as
              | InvalidCredentialsErrorBody
              | undefined;
            clearAuthData(state);
            state.authError = body?.message ?? null;
            state.loginAttempts = body?.data?.attempts ?? null;
            break;
          }
          case 403: {
            const body = fetchError.data as { message?: string } | undefined;
            clearAuthData(state);
            state.authError = body?.message ?? null;
            /**
             * NestJS no manda un código propio para distinguir cuenta
             * bloqueada de pendiente o deshabilitada — los tres casos son
             * `403`, diferenciados solo por el texto (`auth.service.ts`). Es
             * la misma fragilidad que ya tenía Java con `responseMessage`; el
             * único mensaje que incluye esta frase es el de bloqueo por
             * intentos, así que es el único que dispara el modal con cuenta
             * regresiva (`login.modal`).
             */
            state.isBlocked = !!body?.message?.includes("bloqueado por exceder");
            break;
          }
          case 422: {
            const body = fetchError.data as RestorePasswordErrorBody | undefined;
            clearAuthData(state);
            /**
             * Contraseña temporal o vencida: no se abre sesión. Con el token
             * guardado, la aplicación lleva al vendedor directo a la pantalla
             * de cambio (HU-CUDL-088 / CA-07). El `422` también trae una sesión
             * creada en `data.appInfo`; se descarta a propósito, para que nada
             * de la aplicación sea accesible antes del cambio.
             */
            state.passwordChangeToken = body?.data?.passwordChangeToken ?? null;
            break;
          }
          default: {
            const body = fetchError.data as { message?: string } | undefined;
            clearAuthData(state);
            state.authError = body?.message ?? null;
          }
        }
      })
      //Validate status 310 in all backend requests
      .addMatcher(
        promosApi.endpoints.getPromos.matchFulfilled,
        (state, action: PayloadAction<APIBaseResponse>) => {
          const { responseCode } = action.payload.result;
          if (responseCode === 310) {
            state.isAuth = false;
            state.authError = null;
            state.token = null;
            state.user = null;
            state.restorePassword = false;
            state.hashedPass = null;
          }
        }
      )
      .addMatcher(
        propertiesApi.endpoints.getProperties.matchFulfilled,
        (state, action: PayloadAction<APIBaseResponse>) => {
          // Una respuesta 200 que no es el catálogo no trae `result`; leerlo
          // directo lanzaba dentro del reducer (VWA-43).
          if (action.payload?.result?.responseCode === 310) {
            state.isAuth = false;
            state.authError = null;
            state.token = null;
            state.user = null;
            state.restorePassword = false;
            state.hashedPass = null;
          }
        }
      )
      .addMatcher(
        fcmTokensApi.endpoints.registerToken.matchFulfilled,
        (state, action: PayloadAction<APIBaseResponse>) => {
          const { responseCode } = action.payload.result;
          if (responseCode === 310) {
            state.isAuth = false;
            state.authError = null;
            state.token = null;
            state.user = null;
            state.restorePassword = false;
            state.hashedPass = null;
          }
        }
      )
      .addMatcher(
        incentivesApi.endpoints.getIncentives.matchFulfilled,
        (state, action: PayloadAction<APIBaseResponse>) => {
          const { responseCode } = action.payload.result;
          if (responseCode === 310) {
            state.isAuth = false;
            state.authError = null;
            state.token = null;
            state.user = null;
            state.restorePassword = false;
            state.hashedPass = null;
          }
        }
      )
      .addMatcher(
        reportsApi.endpoints.reportActivity.matchFulfilled,
        (state, action: PayloadAction<APIBaseResponse>) => {
          const { responseCode } = action.payload.result;
          if (responseCode === 310) {
            state.isAuth = false;
            state.authError = null;
            state.token = null;
            state.user = null;
            state.restorePassword = false;
            state.hashedPass = null;
          }
        }
      )
      .addMatcher(
        reportsApi.endpoints.reportContentDownloaded.matchFulfilled,
        (state, action: PayloadAction<APIBaseResponse>) => {
          const { responseCode } = action.payload.result;
          if (responseCode === 310) {
            state.isAuth = false;
            state.authError = null;
            state.token = null;
            state.user = null;
            state.restorePassword = false;
            state.hashedPass = null;
          }
        }
      )
      .addMatcher(
        reportsApi.endpoints.reportContentWatched.matchFulfilled,
        (state, action: PayloadAction<APIBaseResponse>) => {
          const { responseCode } = action.payload.result;
          if (responseCode === 310) {
            state.isAuth = false;
            state.authError = null;
            state.token = null;
            state.user = null;
            state.restorePassword = false;
            state.hashedPass = null;
          }
        }
      )
      .addMatcher(
        reportsApi.endpoints.reportIncentive.matchFulfilled,
        (state, action: PayloadAction<APIBaseResponse>) => {
          const { responseCode } = action.payload.result;
          if (responseCode === 310) {
            state.isAuth = false;
            state.authError = null;
            state.token = null;
            state.user = null;
            state.restorePassword = false;
            state.hashedPass = null;
          }
        }
      );
  },
});

export const { savePass, clearLoginData, endPasswordChange } = authSlice.actions;

export const authReducer = authSlice.reducer;

export const selectAuthError = (state: RootState) => state.auth.authError;
export const selectHashedPassword = (state: RootState) => state.auth.hashedPass;
// export const selectPassword = (state: RootState) => state.auth.passUser;
export const selectIsBlockedUser = (state: RootState) => state.auth.isBlocked;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuth;
/**
 * Cambio obligatorio pendiente: hay token del `422` y no hay sesión. Es lo que
 * decide mostrar la pantalla de cambio en lugar del ingreso.
 */
export const selectIsPasswordChangePending = (state: RootState) =>
  !state.auth.isAuth && !!state.auth.passwordChangeToken;
export const selectLoginAttempts = (state: RootState) => state.auth.loginAttempts;
export const selectPasswordChangeToken = (state: RootState) =>
  state.auth.passwordChangeToken;
export const selectRefreshToken = (state: RootState) =>
  state.auth.refreshToken ?? null;
export const selectUser = (state: RootState) => state.auth.user;
export const selectSession = (state: RootState): Session => ({
  sessionToken: state.auth.token,
  userId: state.auth.user?.id,
  username: state.auth.user?.username,
  name: state.auth.user?.name,
  firstLastName: state.auth.user?.firstLastName,
  channel: state.auth.user?.address.channel.name!,
  email: state.auth.user?.email!,
  urlImage: state.auth.user?.urlImage
});
